/*
  Monatskalender der eigenen Verfügbarkeit (Mitarbeitenden-Ansicht).
  Reine Ableitungen aus der Antwort von /api/web/availability/calendar - ohne
  React, damit sie sich ohne Oberfläche prüfen lassen.
*/

import { holidaySaveRange } from "../../availability/availabilityDrafts";

const DAY_MS = 86_400_000;

function dateFromKey(dateKey) {
  return new Date(`${dateKey}T00:00:00`);
}

function keyFromDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/*
  Wochenraster Mo-So. Vor dem Ersten stehen Leerfelder (null), damit der Tag
  unter seinem Wochentag steht; der Server zählt weekday ab Montag = 0.
*/
export function buildMonthCells(days = []) {
  if (!days.length) return [];
  const leading = Math.max(0, Math.min(6, Number(days[0]?.weekday) || 0));
  return [...Array(leading).fill(null), ...days];
}

function timeLabel(entries = []) {
  return entries
    .filter((entry) => !entry.is_full_day && entry.start_time && entry.end_time)
    .map((entry) => `${entry.start_time} - ${entry.end_time}`)
    .join("\n");
}

/*
  Was eine Tageszelle zeigt. "free_request" (Wunschfrei) erscheint wie im
  Editor als Abwesenheit. Ein Tag ohne eigenen Eintrag zeigt den Wochenstandard
  (status_source "default") - die Zelle markiert das gestrichelt.
*/
export function dayDisplay(day) {
  if (!day) return null;
  const entries = day.entries?.length ? day.entries : day.default_entries || [];
  const status = day.status === "free_request" ? "absence" : day.status || "empty";
  return {
    status,
    timeLabel: status === "holiday" ? "" : timeLabel(entries),
    holidayName: status === "holiday" ? day.holidays?.[0]?.holiday_type_name || "" : "",
    isPending: Boolean(day.is_pending),
    isDefault: day.status_source === "default",
    isLocked: day.is_editable === false,
    hasComment: Boolean(day.has_comment),
  };
}

// Alle Tage von anchor bis target (in beliebiger Richtung), beide inklusive.
export function rangeDates(anchorKey, targetKey) {
  if (!anchorKey || !targetKey) return [];
  const [startKey, endKey] = anchorKey <= targetKey ? [anchorKey, targetKey] : [targetKey, anchorKey];
  const dates = [];
  for (let time = dateFromKey(startKey).getTime(); time <= dateFromKey(endKey).getTime(); time += DAY_MS) {
    dates.push(keyFromDate(new Date(time)));
  }
  return dates;
}

function isContiguous(dates) {
  return dates.every(
    (dateKey, index) => index === 0 || rangeDates(dates[index - 1], dateKey).length === 2
  );
}

/*
  PUT-Aufrufe fuer /availability/day - dieselben Regeln wie der Speicherweg
  der Planungsansicht (availability/index.jsx, saveEntry), nur fuer eine
  Person: Ferien als ein Block, sobald der Zeitraum zusammenhaengt oder im
  Editor aufgezogen wurde, sonst ein Aufruf pro Tag.
*/
export function buildDaySaveRequests(draft, selectedDates) {
  const dates = [...(selectedDates || [])].sort();
  if (!draft?.entry_kind || !dates.length) return [];

  const holidayRange = draft.entry_kind === "holiday"
    ? holidaySaveRange({
      draftStartDate: draft.start_date,
      draftEndDate: draft.end_date,
      selectionDates: dates,
      userDates: dates,
      contiguous: isContiguous(dates),
    })
    : null;

  if (holidayRange) {
    return [{
      params: { month: holidayRange.start_date.slice(0, 7), compact: "editor" },
      payload: { ...draft, ...holidayRange, date: holidayRange.start_date },
    }];
  }

  return dates.map((dateKey) => ({
    params: { month: dateKey.slice(0, 7), compact: "editor" },
    payload: {
      ...draft,
      date: dateKey,
      start_date: draft.entry_kind === "holiday" ? dateKey : draft.start_date,
      end_date: draft.entry_kind === "holiday" ? dateKey : draft.end_date,
    },
  }));
}
